// Saúde do sync — classifica o FRESCOR de cada fonte de dado (saldo, vendas,
// notas de entrada, fornecedor) a partir da idade do último registro sincronizado.
// Função pura e determinística: recebe o `lastAt` de cada fonte + um `nowMs`
// estável (capturado 1x no mount, para não violar react-hooks/purity) e devolve
// o status. Obj 1 do SIPOC: consolidar estoque com precisão → transparência de
// reconciliação e alerta de dados velhos.

export type SyncStatus = 'fresh' | 'stale' | 'offline'

export interface SyncSourceInput {
  key: string
  label: string
  /** ISO do registro mais recente da fonte; null = nunca sincronizou. */
  lastAt: string | null
  /** Acima disso vira "stale" (atenção). Em horas. */
  staleAfterH: number
  /** Acima disso vira "offline" (dados velhos / sync parado). Em horas. */
  offlineAfterH: number
  /** Texto curto explicando o que a data representa (ex.: "última venda sincronizada"). */
  hint?: string
}

export interface SyncSourceHealth {
  key: string
  label: string
  lastAt: string | null
  status: SyncStatus
  /** Idade em horas (arredondada); null quando nunca sincronizou. */
  ageH: number | null
  hint?: string
}

const H = 3_600_000

/** Idade em horas de um ISO até `nowMs`; null se sem data. Nunca negativa. */
export function ageInHours(iso: string | null, nowMs: number): number | null {
  if (!iso) return null
  const t = new Date(iso).getTime()
  if (Number.isNaN(t)) return null
  return Math.max(0, (nowMs - t) / H)
}

/** Classifica uma fonte: sem data → offline; jovem → fresh; média → stale; velha → offline. */
export function classifySyncSource(i: SyncSourceInput, nowMs: number): SyncSourceHealth {
  const age = ageInHours(i.lastAt, nowMs)
  let status: SyncStatus
  if (age == null) status = 'offline'
  else if (age <= i.staleAfterH) status = 'fresh'
  else if (age <= i.offlineAfterH) status = 'stale'
  else status = 'offline'
  return { key: i.key, label: i.label, lastAt: i.lastAt, status, ageH: age == null ? null : Math.round(age), hint: i.hint }
}

/** Status agregado = o pior entre as fontes (offline > stale > fresh). */
export function overallSyncStatus(sources: ReadonlyArray<SyncSourceHealth>): SyncStatus {
  if (sources.some(s => s.status === 'offline')) return 'offline'
  if (sources.some(s => s.status === 'stale')) return 'stale'
  return 'fresh'
}

/** Idade legível em pt-BR ("agora", "3 h", "2 d"). null → "nunca". */
export function formatAge(ageH: number | null): string {
  if (ageH == null) return 'nunca'
  if (ageH < 1) return 'agora'
  if (ageH < 24) return `${Math.round(ageH)} h`
  return `${Math.round(ageH / 24)} d`
}

export const SYNC_STATUS_LABEL: Record<SyncStatus, string> = {
  fresh: 'Atualizado',
  stale: 'Atenção',
  offline: 'Desatualizado',
}

/** Cores dos badges (dual-theme), alinhadas ao semáforo do app. */
export const SYNC_STATUS_BADGE: Record<SyncStatus, string> = {
  fresh:   'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300 border border-emerald-500/20',
  stale:   'bg-amber-100 text-amber-800 dark:bg-amber-500/15 dark:text-amber-300 border border-amber-500/20',
  offline: 'bg-red-100 text-red-700 dark:bg-red-500/15 dark:text-red-300 border border-red-500/20',
}

/** Cor do ponto/indicador. */
export const SYNC_STATUS_DOT: Record<SyncStatus, string> = {
  fresh: 'bg-emerald-500',
  stale: 'bg-amber-500',
  offline: 'bg-red-500',
}
